'use client';

import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as React from 'react';
import { cn } from '~/lib/utils';
import { XIcon } from '~/icons';
import { useUserSession } from '~/modules/user/components/impersonate-user/user-session-context';
const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogPortal = DialogPrimitive.Portal;
const DialogClose = DialogPrimitive.Close;
const DialogOverlay = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Overlay>, React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>>(({
  className,
  ...props
}, ref) => <DialogPrimitive.Overlay ref={ref} className={cn('fixed inset-0 z-50 bg-[#112658]/[.42] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0', className)} style={{
  backdropFilter: 'blur(6px)'
}} {...props} />);
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;
const DialogContent = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Content>, React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
  noCloseButton?: boolean;
  hideOverlay?: boolean;
  variant?: 'admin' | 'client';
  closeButtonClassName?: string;
}>(({
  variant,
  children,
  className,
  hideOverlay,
  noCloseButton = false,
  closeButtonClassName,
  ...props
}, ref) => {
  const {
    isClientInterfaceVariant,
    isAdminInterfaceVariant
  } = useUserSession();
  const isClientPortal = isClientInterfaceVariant || variant === 'client';
  return <DialogPortal>
        {!hideOverlay && <DialogOverlay />}
        <DialogPrimitive.Content ref={ref} aria-describedby="" // TODO
    onInteractOutside={event => {
      event.preventDefault();
    }} className={cn('fixed left-1/2 top-1/2 z-50 grid w-full max-w-lg -translate-x-1/2 -translate-y-1/2 gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg', {
      '!rounded-[20px]': isClientPortal
    }, className)} {...props}>
          {children}
          {!noCloseButton && <DialogPrimitive.Close className={cn('absolute right-6 top-6 rounded-sm ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground', {
        'opacity-70': isAdminInterfaceVariant,
        'right-4 top-4 flex size-8 items-center justify-center rounded-full bg-[#F2F6FB] text-primary focus:ring-0 focus:ring-offset-0': isClientPortal
      }, closeButtonClassName)}>
              {isClientPortal ? <div className="rounded-full bg-[#F2F6FB] p-1 text-primary">
                  <XIcon className="size-6 text-primary" strokeWidth="3" />
                </div> : <XIcon className="size-5" strokeWidth={2.5} />}
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>}
        </DialogPrimitive.Content>
      </DialogPortal>;
});
DialogContent.displayName = DialogPrimitive.Content.displayName;
const DialogHeader = ({
  className,
  bottomBorder = false,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  bottomBorder?: boolean;
}) => {
  const {
    isAdminInterfaceVariant,
    isClientInterfaceVariant
  } = useUserSession();
  return <div className={cn('-mx-6 -mt-6 flex flex-col space-y-1.5 overflow-x-hidden p-6 text-center sm:text-left', {
    'border-b': isAdminInterfaceVariant || bottomBorder === true,
    'bg-custom-lightBlue': isAdminInterfaceVariant,
    'min-h-[80px] justify-center': isClientInterfaceVariant
  }, className)} {...props} data-sentry-component="DialogHeader" data-sentry-source-file="dialog.tsx" />;
};
DialogHeader.displayName = 'DialogHeader';
const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => <div className={cn('-mx-6 flex flex-col-reverse gap-y-2 border-t px-6 pt-6 sm:flex-row sm:justify-between sm:gap-y-0 sm:space-x-2', className)} {...props} data-sentry-component="DialogFooter" data-sentry-source-file="dialog.tsx" />;
DialogFooter.displayName = 'DialogFooter';
const DialogTitle = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Title>, React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>>(({
  className,
  ...props
}, ref) => {
  const {
    isAdminInterfaceVariant,
    isClientInterfaceVariant
  } = useUserSession();
  return <DialogPrimitive.Title ref={ref} className={cn({
    'text-lg font-semibold leading-none tracking-tight': isAdminInterfaceVariant,
    'font-fredoka text-xl font-semibold leading-none text-primary': isClientInterfaceVariant
  }, className)} {...props} />;
});
DialogTitle.displayName = DialogPrimitive.Title.displayName;
const DialogDescription = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Description>, React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>>(({
  className,
  ...props
}, ref) => <DialogPrimitive.Description ref={ref} className={cn('text-sm text-muted-foreground', className)} {...props} />);
DialogDescription.displayName = DialogPrimitive.Description.displayName;
export { Dialog, DialogPortal, DialogOverlay, DialogClose, DialogTrigger, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription };